<template>
  <form
    :ref="dropzoneRef"
    class="dropzone"
    :class="[dropzoneClassName]"
    :data-ua-unique-id="dataUaUniqueId"
    @drop="onDrop"
    @change="onChange"
    @dragover="handleDragOver"
  >
    <div
      v-if="ids.length === 0"
      class="dropzone__message"
      :class="[dropzoneMessageClassName]"
    >
      <slot name="message">Drag and Drop file here</slot>
    </div>
    <div
      v-for="(item, itemId) in all"
      :key="itemId"
      class="dropzone__item"
      :class="[
        {
          'dropzone--has-thumbnail': !!item.thumbnail,
          'dropzone--added': item.status === 'ADDED',
          'dropzone--processing': item.status === 'UPLOADING',
          'dropzone--success': item.status === 'DONE',
          'dropzone--has-error': item.status === 'ERROR',
        },
        dropzoneItemClassName,
      ]"
    >
      <div class="dropzone__item-thumbnail">
        <img v-if="item.thumbnail" :src="item.thumbnail" />
      </div>
      <div class="dropzone__item-controls">
        <div class="dropzone__item-control" @click="removeFile(itemId)">
          <slot name="remove"><i class="gg-close"></i></slot>
        </div>
      </div>
      <!-- <div class="dropzone__progress">
        <progress
          class="dropzone__progress-bar"
          max="100"
          :value.prop="item.upload.progress"
        />
      </div> -->
      <div class="dropzone__success-mark">
        <slot name="success"><i class="gg-check-o"></i></slot>
      </div>
      <div class="dropzone__error-mark">
        <slot name="error"><i class="gg-danger"></i></slot>
      </div>
      <div class="dropzone__details" :class="[dropzoneDetailsClassName]">
        <div class="dropzone__file-size">
          <span v-html="filesize(item.file.size)"></span>
        </div>
        <div class="dropzone__filename">
          <span>{{ item.file.name }}</span>
        </div>
      </div>
    </div>
  </form>
</template>
<script lang="ts">
import {
  defineComponent,
  onMounted,
  onUnmounted,
  ref,
  toRefs,
  watch,
  nextTick,
} from 'vue'
import { determineDragAndDropCapable, filesize } from './utils'
import useDragAndDrop from './hooks/drag'
import useHiddenInputFile from './hooks/hiddenIpuntFile'
import dropzoneProps from './props'
import useConfig from './hooks/config'
import useItemManager from './hooks/itemManager'

// TODO - custom style for progress bar
// TODO - custom style for name and size
// TODO - disable
// TODO - Understand capture
export default defineComponent({
  name: 'lp-file-picker',

  props: {
    ...dropzoneProps,
    dropzoneRef: {
      type: String,
      default: 'dropzone',
    },
    dropzoneClassName: {
      type: String,
      default: 'dropzone__box',
    },
    dropzoneMessageClassName: {
      type: String,
      default: 'dropzone__message--style',
    },
    dropzoneItemClassName: {
      type: String,
      default: 'dropzone__item--style',
    },
    dropzoneDetailsClassName: {
      type: String,
      default: 'dropzone__details--style',
    },
    dataUaUniqueId: {
      type: String,
      default: 'unique-file-picker',
    },
  },
  emits: [
    'config-update',
    'added-file',
    'added-all-files',
    'removed-file',
    'error-upload',
    'uploaded',
    'uploaded-all',
    'sending',
    'error-add',
  ],
  setup(props, context) {
    const dropzone = ref()
    const dropzoneRef = ref()
    const dropzoneContainer = ref(props.hiddenInputContainer)

    const dragAndDropCapable = ref(true)
    // --------------- Hidden input file feature
    const {
      // Create the hidden input file
      initHiddenFileInput,
      // Destroy from the dom the hidden input file
      destroyHiddenFileInput,
      // set multiple attribute
      setMultiple,
      triggerClickOnHiddenFileInput,
    } = useHiddenInputFile()
    // --------------- End hidden input file feature

    // DropZone configuration reactive object
    const { config } = useConfig({ props, context, setMultiple })

    const { itemManager } = useItemManager({ config, context })

    // Drag and drop file feature
    const { handleDragOver, onDrop } = useDragAndDrop({
      itemManager,
      context,
    })

    const onChange = (e) => {
      context.emit('added-all-files', e.target.files)
    }

    // If the items list is empty we need to re-create the hidden file input.
    watch(
      () => itemManager.getItems(),
      (val) => {
        if (val.ids.length === 0) {
          nextTick(() => {
            destroyHiddenFileInput()
            initHiddenFileInput({
              config,
              dropzone,
              itemManager,
            })
          })
        }
      },
      { deep: true },
    )
    watch(
      () => props.clickable,
      (val) => {
        if (!val) {
          nextTick(() => {
            destroyHiddenFileInput()
          })
        }
      },
      { deep: true },
    )
    onMounted(() => {
      dragAndDropCapable.value = determineDragAndDropCapable()
      config.hiddenInputContainer = dropzone.value
      initHiddenFileInput({
        config,
        dropzone,
        itemManager,
      })
    })
    onUnmounted(() => {
      destroyHiddenFileInput()
    })
    return {
      ...toRefs(itemManager.getItems()),
      accepts: config.accepts,
      onDrop,
      handleDragOver,
      removeFile: itemManager.removeFile,
      filesize,
      dropzone,
      onChange,
    }
  },
})
</script>
